import { Edit } from '@mui/icons-material'
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  MenuItem,
  Chip as MuiChip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from '@mui/material'
import { GreenText, SortableTableCell } from 'components'
import { Chip } from 'components/chips'
import { ListFilterGroup } from 'components/list'
import * as controllers from 'controllers'
import { VehicleDamageDto, VehicleDto } from 'dtos'
import { DEBOUNCE_DELAY } from 'localConstants'
import moment from 'moment'
import { VehicleDamageParameters } from 'parameters/vehicleDamageParameters'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDateTime, useDebounce } from 'utils'

interface DamageDetailsListCardProps {
  vehicle: VehicleDto
  onEditDamage: (damage: string) => void
}

export default function DamageDetailsListCard({ vehicle, onEditDamage }: DamageDetailsListCardProps) {
  const navigate = useNavigate()

  const [isGettingDamages, setIsGettingDamages] = useState<boolean>(false)
  const [damages, setDamages] = useState<VehicleDamageDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)

  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')

  const [parameters, setParameters] = useState<VehicleDamageParameters>({
    page: 0,
    pageSize: 10,
    orderBy: 'createdDate',
    order: 'asc',
    damageStatus: 'Unresolved'
  })

  const onChangeSortByDirection = (orderBy: string) => {
    setParameters(prev => ({
      ...parameters,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  useEffect(() => {
    if (vehicle.vehicleGuid) {
      getDamages(vehicle.vehicleGuid, parameters)
    }
  }, [vehicle.vehicleGuid, parameters])

  const getDamages = useDebounce((vehicleGuid: string, parameters: VehicleDamageParameters) => {
    setIsGettingDamages(true)
    controllers
      .getVehicleDamagesByVehicleGuid(vehicleGuid, parameters)
      .then(response => {
        setDamages(response.value)
        setTotalCount(response.totalCount!)
      })
      .finally(() => {
        setIsGettingDamages(false)
      })
  }, DEBOUNCE_DELAY)

  const fieldMap: Record<string, keyof VehicleDamageParameters> = {
    importBranch: 'importBranch',
    exporter: 'exporterCompany',
    buyer: 'buyerCompany',
    createdBy: 'createdBy',
    updatedBy: 'updatedBy'
  }

  return (
    <Grid item xs={12}>
      <Card variant='outlined'>
        <CardContent>
          <Grid container spacing={1} alignItems='center'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={true} container spacing={1} alignItems='center'>
                <Grid item xs={12} sm='auto'>
                  <TextField
                    fullWidth
                    label='Filter Field'
                    margin='dense'
                    onChange={e => {
                      const field = e.target.value
                      setFilterField(field)
                      setFilterValue('')
                      setFilterValueFrom('')
                      setFilterValueTo('')

                      if (field === 'resolved' || field === 'unresolved') {
                        const status = field === 'resolved' ? 'Resolved' : 'Unresolved'
                        setParameters({ ...parameters, damageStatus: status, page: 0 })
                      }
                    }}
                    select
                    size='small'
                    sx={{ minWidth: 200 }}
                    value={filterField || ''}
                  >
                    <MenuItem value='resolved'>
                      <Chip variant='green'>Resolved</Chip>
                    </MenuItem>

                    <MenuItem value='unresolved'>
                      <Chip variant='red'>Unresolved</Chip>
                    </MenuItem>

                    <MenuItem value='importBranch'>Import Branch</MenuItem>

                    <MenuItem value='exporter'>Exporter</MenuItem>

                    <MenuItem value='buyer'>Buyer</MenuItem>

                    <MenuItem value='createdDate'>Created Date</MenuItem>

                    <MenuItem value='createdBy'>Created By</MenuItem>

                    <MenuItem value='updatedDate'>Updated Date</MenuItem>

                    <MenuItem value='updatedBy'>Updated By</MenuItem>
                  </TextField>
                </Grid>

                {['', 'resolved', 'unresolved', 'importBranch', 'exporter', 'buyer', 'createdBy', 'updatedBy'].includes(
                  filterField
                ) && (
                  <Grid item xs={12} sm='auto'>
                    <TextField
                      label='Filter Value'
                      size='small'
                      value={filterValue}
                      onChange={e => setFilterValue(e.target.value)}
                      margin='dense'
                      disabled={filterField === 'resolved' || filterField === 'unresolved'}
                    />
                  </Grid>
                )}

                {['createdDate', 'updatedDate'].includes(filterField) && (
                  <>
                    <Grid item xs={12} sm='auto'>
                      <TextField
                        label='Date From'
                        type='date'
                        margin='dense'
                        InputLabelProps={{ shrink: true }}
                        size='small'
                        value={filterValueFrom}
                        onChange={e => setFilterValueFrom(e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} sm='auto'>
                      <TextField
                        label='Date To'
                        type='date'
                        margin='dense'
                        InputLabelProps={{ shrink: true }}
                        size='small'
                        value={filterValueTo}
                        onChange={e => setFilterValueTo(e.target.value)}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='inherit'
                    disabled={
                      !filterField ||
                      (filterField === 'createdDate' || filterField === 'updatedDate'
                        ? !filterValueFrom || !filterValueTo
                        : !filterValue)
                    }
                    variant='contained'
                    onClick={() => {
                      if (filterField === 'createdDate') {
                        setParameters({
                          ...parameters,
                          createdDateFrom: filterValueFrom,
                          createdDateTo: filterValueTo,
                          page: 0
                        })
                      } else if (filterField === 'updatedDate') {
                        setParameters({
                          ...parameters,
                          updatedDateFrom: filterValueFrom,
                          updatedDateTo: filterValueTo,
                          page: 0
                        })
                      } else {
                        setParameters({
                          ...parameters,
                          [fieldMap[filterField] as keyof VehicleDamageParameters]: filterValue
                        })
                      }

                      setFilterField('')
                      setFilterValue('')
                      setFilterValueFrom('')
                      setFilterValueTo('')
                    }}
                  >
                    APPLY FILTER
                  </Button>
                </Grid>

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='secondary'
                    disabled={
                      !parameters.importBranch ||
                      !parameters.exporterCompany ||
                      !parameters.buyerCompany ||
                      !parameters.createdDate ||
                      !parameters.createdBy ||
                      !parameters.updatedDate ||
                      !parameters.updatedBy ||
                      !parameters.damageStatus
                    }
                    size='medium'
                    variant='text'
                    onClick={() => {
                      setParameters({
                        ...parameters,
                        damageStatus: undefined,
                        importBranch: undefined,
                        exporterCompany: undefined,
                        buyerCompany: undefined,
                        createdDateFrom: undefined,
                        createdDateTo: undefined,
                        createdBy: undefined,
                        updatedDateFrom: undefined,
                        updatedDateTo: undefined,
                        updatedBy: undefined,
                        page: 0
                      })

                      setFilterField('')
                      setFilterValue('')
                      setFilterValueFrom('')
                      setFilterValueTo('')
                    }}
                  >
                    RESET FILTERS
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} container spacing={1} alignItems='center'>
              {parameters.damageStatus && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Status is "${parameters.damageStatus}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        damageStatus: undefined,
                        page: 0
                      })
                      setFilterField('')
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.importBranch && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Import Branch is "${parameters.importBranch}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        importBranch: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.exporterCompany && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Exporter contains "${parameters.exporterCompany}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        exporterCompany: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.buyerCompany && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Buyer contains "${parameters.buyerCompany}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        buyerCompany: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.createdDateFrom && parameters.createdDateTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Created Date from "${moment(parameters.createdDateFrom).format('M/D/yyyy')}" to "${moment(
                      parameters.createdDateTo
                    ).format('M/D/yyyy')}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        createdDateFrom: undefined,
                        createdDateTo: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.createdBy && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Created by contains "${parameters.createdBy}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        createdBy: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.updatedDateFrom && parameters.updatedDateTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Updated Date from "${moment(parameters.updatedDateFrom).format('M/D/yyyy')}" to "${moment(
                      parameters.updatedDateTo
                    ).format('M/D/yyyy')}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        updatedDateFrom: undefined,
                        updatedDateTo: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.updatedBy && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Updated by contains "${parameters.updatedBy}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        updatedBy: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}
            </Grid>

            {isGettingDamages && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingDamages && (
              <Grid item xs={12}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='importBranch'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Import Branch
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='exporter'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Exporter
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='buyer'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Buyer
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='createdDate'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Created Date
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='createdBy'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Created By
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='updatedDate'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Updated Date
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='updatedBy'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Updated By
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='damageStatus'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Status
                        </SortableTableCell>

                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {damages
                        .filter(damage => damage.vehicleGuid === vehicle.vehicleGuid)
                        .map(damage => (
                          <TableRow>
                            <TableCell>{damage.importBranch?.name || '-'}</TableCell>
                            <TableCell>{damage.exporterCompany?.name || '-'}</TableCell>
                            <TableCell>{damage.buyerCompany?.name || '-'}</TableCell>
                            <TableCell>{damage.createdDateTime ? formatDateTime(damage.createdDateTime) : ''}</TableCell>
                            <TableCell>
                              {damage.createdBy?.firstName} {damage.createdBy?.lastName}
                            </TableCell>
                            <TableCell>{damage.updatedDateTime ? formatDateTime(damage.updatedDateTime) : ''}</TableCell>
                            <TableCell>
                              {damage.updatedBy?.firstName} {damage.updatedBy?.lastName}
                            </TableCell>
                            <TableCell>{damage.damageStatus}</TableCell>
                            <TableCell>
                              <Button
                                color='primary'
                                onClick={() => {
                                  navigate(`/vehicles/${vehicle.vehicleGuid}/damage-details/${damage.vehicleDamageGuid}`)
                                }}
                                variant='text'
                                endIcon={<Edit />}
                              >
                                Edit
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25]}
                          SelectProps={{ fullWidth: false }}
                          count={totalCount}
                          rowsPerPage={parameters.pageSize}
                          page={parameters.page}
                          onPageChange={(_, page) => {
                            setParameters({
                              ...parameters,
                              page
                            })
                          }}
                          onRowsPerPageChange={e => {
                            setParameters({
                              ...parameters,
                              page: 0,
                              pageSize: e.target.value as unknown as number
                            })
                          }}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              </Grid>
            )}
            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Filter Field Drop-Down**
                  
Clicking the drop-down displays a list of available columns to filter by:

- Resolved
- Unresolved
- Import Branch
- Exporter
- Buyer
- Created Date
- Created By
- Updated Date
- Updated By

**Filter Value:**

The "Filter Value" field dynamically adjusts based on the selected column in the "Filter Field Drop-Down":

- Resolved: Displays all damage records that have been resolved.
- Unresolved: Displays all damage records that have not been resolved. (This is the default filter option).
- Initial Damage Date: Displays Filter Date From and Filter Date To.
- Exporter: Displays a free-text filter field.
- Buyer: Displays a free-text filter field.
- Created Date: Displays Filter Date From and Filter Date To.
- Created By: Displays a free-text filter field.
- Updated Date: Displays Filter Date From and Filter Date To.

**Apply Filter Button:**

Clicking this button applies the selected filtering options and updates the displayed data.

**Reset Filters:**

Clicking this button clears all applied filtering options, returning the data view to its default state.

**Edit Button:**

Clicking the Edit Button takes the user to the Damage Details Screen.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  )
}
